import React, { useEffect, useRef, useState } from 'react';
import { BulletIcon, PelletsIcon, PlusIcon } from './Icons';
import { Link, NavLink } from 'react-router-dom';
import { Color, Placement } from 'react-bootstrap/esm/types';
import { Col, Row, Alert, Form, Fade, Modal, Badge, Button, Table, Accordion, Popover, ProgressBar, ToggleButton, FloatingLabel, OverlayTrigger, ToggleButtonGroup } from 'react-bootstrap';



interface FadeComponentProps {
  children: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
}

export const FadeIn: React.FC<FadeComponentProps> = ({ children }) => {
  const useFadeIn = () => {
    const [fadeIn, setIn] = useState(false);
    useEffect(() => {setIn(true)}, []);
    return fadeIn;
  };
  const enter = useFadeIn();
  return <Fade in={enter}>{children}</Fade>;
};


interface CrumbProps {
  to: string;
  base?: boolean;
  children: React.ReactNode;
}

export const Crumb: React.FC<CrumbProps> = ({ to, children }) => (
  <NavLink to={to} className={({ isActive }) => '' + (isActive ? '' : 'd-none')}>
    <span className='mx-2'>/</span>
    {children}
  </NavLink>
);



interface ActiveToggleProps {
  toggle: number;
  toggle1?: string;
  toggle2?: string;
  onChange: (value: any) => void;
}

export const ActiveToggle: React.FC<ActiveToggleProps> = ({ onChange, toggle, toggle1 = 'Active', toggle2 = 'Archived' }) => (
  <ToggleButtonGroup name='options' defaultValue={1} value={toggle} onChange={onChange} className='float-end'>
    <ToggleButton id='tbg-1' value={1} variant={'outline-primary'} style={{ minWidth: 120 }} className={' py-2 me-3 '}>{toggle1}</ToggleButton>
    <ToggleButton id='tbg-2' value={2} variant={'outline-primary'} style={{ minWidth: 120 }} className={' py-2 '}>{toggle2}</ToggleButton>
  </ToggleButtonGroup>
);


interface NotificationProps {
  date: string;
  link?: string;
  type: string; 
  project: string;
  canopyid?:number;
  isRead?: boolean; 
  projectid:string;
}

export const Notification: React.FC<NotificationProps> = ({
  date,
  type,
  project,
  canopyid,
  projectid,
  isRead = false,
}) => {
  // Function to determine the message based on the type
  const getMessage = (type: string,project:string) => {
    switch (type) {
      case 'Messaging':
        return `New message in ${project}`;
     case 'Project Scheduled':
      return `${project} is Scheduled for Testing`;
       case 'In Progress':
      return `${project} testing is in progress`;
       case 'Report Available':
      return `${project} Reports are available`;
      case 'Quote Available':
        return `Quote Available for ${project}`;
      default:
        return `No new notifications`; 
    }
  };
  
  const getLink = (type: string, projectid: string, canopyid?: number) => {
    switch (type) {
      case 'Messaging':
        return `/messaging`
        case 'Project Scheduled':
          return `projects/${projectid}/${canopyid}`
           case 'In Progress':
          return `projects/${projectid}/${canopyid}`
           case 'Report Available':
          return `projects/${projectid}/${canopyid}`
      case 'Quote Available':
        return `projects/${projectid}/quote`
      default:
        return `#`; 
    }
  };

  return (
    <Link to={getLink(type, projectid, canopyid)}>
      <Row className={`px-3 py-3 border-gray border-bottom ${isRead ? '' : 'bg-basics'}`}>
        <Col sm={11} className='my-auto'>
          <div>{getMessage(type, project)}</div>
          <h6 className='m-0 mt-2 small text-gray'>{date}</h6>
        </Col>
        <Col sm={1} as='h2' className='m-0'>
          <BulletIcon className={isRead ? '' : 'text-secondary'} width={16} />
        </Col>
      </Row>
    </Link>
  );
};



interface ActivityProps {
  date: string;
  link?: string;
  text: string;
  className?: string;
}

export const Activity: React.FC<ActivityProps> = ({ date, text, link = null, className }) => (
  <Link to='#'>
    <Row className={'p-2 mt-0 ' + className}>
      <Col sm={12} className='my-auto small'>
        {text}
        <h6 className='m-0 small text-gray'>{date}</h6>
      </Col>
    </Row>
  </Link>
);



interface ChatProps {
  sent?: boolean;
  children: React.ReactNode;
}

export const Chat: React.FC<ChatProps> = ({ children, sent = false }) => (
  <div className={sent ? 'text-end' : ''}>
    <div className={'d-inline-block rounded-pill px-4 py-3 mb-3 shadow ' + (sent ? ' bg-secondary text-white' : ' bg-white')}>
      {children}
    </div>
  </div>
);



interface NavButtonProps {
  to: string;
  // Two overrides, because NavLink's own matching cannot express either case. `inactive` forces
  // an item off where NavLink would call it active — it treats any descendant path as a match, so
  // a parent destination stays lit while a nested one is being viewed. `active` forces one on for
  // a sibling route it does not point at, so a multi-page flow keeps one item selected throughout.
  inactive?: boolean;
  active?: boolean;
  children: React.ReactNode;
}

export const NavButton: React.FC<NavButtonProps> = ({ to, inactive, active, children }) => (
  <NavLink to={to} className={({ isActive }) => 'nav-link' + ((isActive || active) && !inactive ? ' active' : '')}>{children}</NavLink>
);



interface FadeAccordionProps {
  className?: string;
  alwaysOpen?: boolean;
  // An array opens several panels at once. `alwaysOpen` only PERMITS that — it opens nothing on
  // its own — so a multi-panel accordion that should start expanded needs every key listed here.
  defaultActiveKey?: string | string[];
  // Pass both to drive the accordion yourself. Needed when what a panel renders is expensive
  // enough to want deferring until it is actually opened — react-bootstrap mounts a collapsed
  // panel's children regardless, so an uncontrolled accordion cannot express that.
  activeKey?: string | string[];
  onSelect?: (key: string | string[] | null | undefined) => void;
  children: React.ReactNode;
}

export const FadeAccordion: React.FC<FadeAccordionProps> = ({
  className,
  alwaysOpen,
  defaultActiveKey,
  activeKey,
  onSelect,
  children,
}) => {
  return (
    <FadeIn>
      <Accordion
        defaultActiveKey={defaultActiveKey}
        activeKey={activeKey}
        onSelect={onSelect as any}
        alwaysOpen={alwaysOpen}
        className={`fade-accordion position-relative ${className || ""}`}
        style={{ overflow: "visible", zIndex: 0 }}
      >
        {children}
      </Accordion>
    </FadeIn>
  );
};


interface FadeAccordionItemProps {
  eventKey: string;
  className?: string;
  small?: boolean;
  noAccents?: boolean;
  children: React.ReactNode; // you render Header/Body inside
}

export const FadeAccordionItem: React.FC<FadeAccordionItemProps> = ({
  eventKey,
  className,
  small,
  noAccents,
  children,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const item = itemRef.current;
    if (!item) return;

    // mount flag for entry motion
    const mountTimer = setTimeout(() => item.classList.add("mounted"), 50);

    // target *this* panel’s collapse node
    const collapse = item.querySelector<HTMLElement>(":scope > .accordion-collapse");

    const setExpandedFromDOM = () => {
      const isOpen = collapse?.classList.contains("show");
      item.classList.toggle("expanded", !!isOpen);
    };

    setExpandedFromDOM();

    // 1) listen directly on the collapse node (not the item)
    const onShow = setExpandedFromDOM as EventListener;
    const onShown = setExpandedFromDOM as EventListener;
    const onHide = setExpandedFromDOM as EventListener;
    const onHidden = setExpandedFromDOM as EventListener;

    collapse?.addEventListener("show.bs.collapse", onShow);
    collapse?.addEventListener("shown.bs.collapse", onShown);
    collapse?.addEventListener("hide.bs.collapse", onHide);
    collapse?.addEventListener("hidden.bs.collapse", onHidden);

    // 2) mutation observer as a safety net (class changes)
    const mo = collapse
      ? new MutationObserver(setExpandedFromDOM)
      : null;
    mo?.observe(collapse!, { attributes: true, attributeFilter: ["class"] });

    return () => {
      clearTimeout(mountTimer);
      collapse?.removeEventListener("show.bs.collapse", onShow);
      collapse?.removeEventListener("shown.bs.collapse", onShown);
      collapse?.removeEventListener("hide.bs.collapse", onHide);
      collapse?.removeEventListener("hidden.bs.collapse", onHidden);
      mo?.disconnect();
    };
  }, []);

  return (
    <Accordion.Item
      eventKey={eventKey}
      ref={itemRef as any}
      className={`fade-accordion-item ${small ? "small" : ""} ${noAccents ? "no-accents" : ""} ${className || ""}`}
      style={{ position: "relative" }}
    >
      {!noAccents && (
        <>
          <svg className="accent-top-item" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 665 68" aria-hidden="true">
            <path d="M.969,0,665,11.241,664.031,68,0,56.759Z" fill="#05ffe0" />
          </svg>
          <svg className="accent-top-item small" xmlns="http://www.w3.org/2000/svg" width="395.028" viewBox="0 0 395.028 63.276" aria-hidden="true">
            <rect width="393.538" height="49.572" transform="matrix(0.999, 0.035, -0.035, 0.999, 1.73, 0)" fill="#05ffe0" />
          </svg>

          <svg className="accent-bottom-item" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 665 68" aria-hidden="true">
            <path d="M.969,0,665,11.241,664.031,68,0,56.759Z" fill="#05ffe0" />
          </svg>
          <svg className="accent-bottom-item small" xmlns="http://www.w3.org/2000/svg" width="395.028" viewBox="0 0 395.028 63.276" aria-hidden="true">
            <rect width="393.538" height="49.572" transform="matrix(0.999, 0.035, -0.035, 0.999, 1.73, 0)" fill="#05ffe0" />
          </svg>
        </>
      )}

      {children}
    </Accordion.Item>
  );
};


interface FadeTableProps {
  className?: string;
  children: React.ReactNode;
  borderless?: boolean;
  hover?: boolean;
  noAccents?: boolean;
  small?: boolean;
}

export const FadeTable: React.FC<FadeTableProps> = ({
  className = "",
  children,
  borderless,
  hover = true,
  small = false,
  noAccents = false,
}) => {
  const ref = useRef<HTMLTableElement>(null);

  useEffect(() => {
    const table = ref.current?.closest(".fade-table");
    if (!table) return;
    const mountTimer = setTimeout(() => table.classList.add("mounted"), 50);
    return () => clearTimeout(mountTimer);
  }, []);

  const AccentTop = small ? (
    <svg
        className="accent-top d-none d-lg-inline-block"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 665 68"
      >
        <path
          d="M.969,0,665,11.241,664.031,68,0,56.759Z"
          fill="#05ffe0"
        />
      </svg>
  ) : (
    <>
      <svg
        className="accent-top small"
        xmlns="http://www.w3.org/2000/svg"
        width="395.028"
        viewBox="0 0 395.028 63.276"
      >
        <rect
          width="393.538"
          height="49.572"
          transform="matrix(0.999, 0.035, -0.035, 0.999, 1.73, 0)"
          fill="#05ffe0"
        />
      </svg>
      <svg
        className="accent-top d-none d-lg-inline-block"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 665 68"
      >
        <path
          d="M.969,0,665,11.241,664.031,68,0,56.759Z"
          fill="#05ffe0"
        />
      </svg>
    </>
  );

  const AccentBottom = small ? (
    <svg
      className="accent-bottom d-lg-inline-block"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 665 68"
    >
      <path
        d="M.969,0,665,11.241,664.031,68,0,56.759Z"
        fill="#05ffe0"
      />
    </svg>
  ) : (
    <>
    <svg
      className="accent-bottom small d-lg-none"
      xmlns="http://www.w3.org/2000/svg"
      width="395.028"
      viewBox="0 0 395.028 63.276"
    >
      <rect
        width="393.538"
        height="49.572"
        transform="matrix(0.999, 0.035, -0.035, 0.999, 1.73, 0)"
        fill="#05ffe0"
      />
    </svg>
    
    <svg
      className="accent-bottom d-lg-inline-block"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 665 68"
    >
      <path
        d="M.969,0,665,11.241,664.031,68,0,56.759Z"
        fill="#05ffe0"
      />
    </svg>
    </>
  );

  return (
    <FadeIn>
      <div className={`fade-table position-relative ${small ? "small" : ""} ${className}`}>

        {/* Render the .table-responsive wrapper ourselves (instead of <Table responsive>)
            so the decorative accent <svg>s sit as siblings of <table> inside the wrapper,
            rather than as invalid direct children of <table>. The accent CSS is scoped to
            `.table-responsive .accent-*`, so it still matches unchanged. */}
        <div className="table-responsive">
          {children && !noAccents && AccentTop}
          <Table
            ref={ref}
            hover={hover}
            borderless={borderless}
            className="mb-5 rounded-1 bg-white shadow"
          >
            {children}
          </Table>
          {children && !noAccents && AccentBottom}
        </div>

      </div>
    </FadeIn>
  );
};


interface FadeModalProps {
  show: boolean;
  title?: string;
  onHide?: () => void;
  body?: React.ReactNode;
  size?: 'sm' | 'lg' | 'xl' |null;
  children?: React.ReactNode | null;
}

export const FadeModal: React.FC<FadeModalProps> = ({ size = null, body, title, show, onHide, children = null }) => {
  return (
    <>
      <Modal show={show} onHide={onHide} backdrop='static' size={size} keyboard={false} restoreFocus>
        {title ? <Modal.Header closeButton className='h1 m-0'>{title}</Modal.Header> : null}
        {body ? body : children}
      </Modal>
      {body && children}
    </>
  );
};



interface FadeAlertProps {
  show?: boolean;
  dismissFunction: () => void;
  children?: React.ReactNode | null;
}

export const FadeAlert: React.FC<FadeAlertProps> = ({ dismissFunction, children = null, show }) => (
  show && <Alert variant='danger'  className='mb-4' onClose={dismissFunction} dismissible>{children ?? 'There was an error loading the details.'}</Alert>
);
interface FadeAlertNoDataProps {
  show?: boolean;
  dismissFunction: () => void;
  children?: React.ReactNode | null;
}

export const FadeNoDataAlert: React.FC<FadeAlertNoDataProps> = ({ dismissFunction, children = null, show }) => (
  show && <Alert variant='danger' onClose={dismissFunction} dismissible>{children ?? 'There is no client data available.'}</Alert>
);



interface AnimatedProgressProps {
  width?: string;
  height?: string;
  variant?: string;
  className?: string;
  initialProgress: number;
}

export const AnimatedProgress: React.FC<AnimatedProgressProps> = ({
  width = '',
  height = '',
  className = '',
  initialProgress,
}) => {
  const [progress, setProgress] = useState(0);
  const styles: React.CSSProperties = { transform: 'translateZ(0)', transition: 'width 1.2s ease-in-out' };
  useEffect(() => { const t = setTimeout(() => setProgress(initialProgress), 200); return () => clearTimeout(t) }, [initialProgress]);
  return <ProgressBar now={progress} className={className} style={{ ...styles, height, width }} />;
};



interface CountUpNumberProps {
  number: number;
  speed?: number;
}

export const CountUpNumber: React.FC<CountUpNumberProps> = ({ number, speed = 50 }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const int = setInterval(() => count < number && setCount(c => c + 1), speed);
    return () => clearInterval(int)
  }, [count, number, speed]);
  return <>{count}</>;
};



interface TableLinkProps {
  colSpan?: number;
  centered?: boolean;
  className?: string;
  to?: string | null;
  scrollTop?: boolean;
  children?: React.ReactNode;
  onClick?: () => void | null;
  disabled?: boolean;
}

export const TableLink: React.FC<TableLinkProps> = ({
  scrollTop,
  colSpan = 0,
  children = null,
  to = null,
  centered = false,
  className = '',
  onClick = null,
  disabled = false
}) => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  return (
    <tr className={className}>
      {colSpan > 0 && <td colSpan={colSpan} />}
      <td colSpan={100} className={centered ? 'text-center' : ''}>
        {to ?
          <Link to={to} onClick={scrollTop && scrollToTop} className='h5 stretched-link text-dark'>
            {!centered && <PlusIcon />}
            {children}
          </Link>
          :
          <Button variant='link' size='sm' className='h5 p-0 m-0 text-secondary stretched-link' onClick={onClick} disabled={disabled}>
            {!centered && <PlusIcon />}
            {children}
          </Button>
        }
      </td>
    </tr>
  );
};


interface LinkButtonProps {
  variant?: string;
  className?: string;
  to?: string | null;
  children?: React.ReactNode;
  onClick?: () => void | null;
  disabled?:boolean;
}

export const LinkButton: React.FC<LinkButtonProps> = ({
  to,
  onClick,
  className = '',
  children,
  variant = 'secondary',
  disabled = false, // Default to false if not passed
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      return; // Stop further execution if disabled
    }
    onClick?.(); // Call onClick if provided
  };

  return to ? (
    <Link
      to={disabled ? '#' : to} // Set a dummy link if disabled
      className={`btn btn-${variant} ${className} ${disabled ? 'disabled' : ''}`}
      onClick={handleClick}
    >
      {children}
    </Link>
  ) : (
    <Button
      variant={variant}
      onClick={handleClick}
      disabled={disabled}
      className={className}
    >
      {children}
    </Button>
  );
};




interface OverlayLinkProps {
  message?: string;
  disabled?: boolean;
  className?: string;
  to?: string | null;
  onClick?: () => void;
  placement?: Placement;
  body?: React.ReactNode | null;
  children?: React.ReactNode | null;
}

export const OverlayLink: React.FC<OverlayLinkProps> = ({ onClick, disabled, placement = 'right', children, to, className, body, message = 'Sorry. This project can no longer be edited or deleted.' }) => {
  const popover = (<Popover><Popover.Body>{message}</Popover.Body></Popover>);
  return (
    <>
      {!disabled ?
        (to ?
          <Link to={to} className={className}>{body}</Link>
          :
          <Link to={'#'} className={className} onClick={onClick}>{body}</Link>
        ) : (
          <OverlayTrigger placement={placement} overlay={popover} delay={{ show: 800, hide: 200 }}>
            <div className='position-relative'><Button disabled variant='link' className='p-0'>{body}</Button></div>
          </OverlayTrigger>
        )}
      {children}
    </>
  );
};


interface LinkBadgeProps {
  bg?: string;
  text?: Color;
  border?: string;
  bullet?: boolean;
  className?: string;
  to?: string | null;
  onClick?: () => void | null;
  children?: React.ReactNode | null;
}

export const LinkBadge: React.FC<LinkBadgeProps> = ({
  bullet,
  onClick,
  children,
  to = null,
  text = 'primary',
  className = 'ms-n2',
  bg = 'primary-subtle',
  border = 'primary-subtle',
}) => {
  return (to ?
    <Link to={to}>
      <Badge bg={bg} text={text} className={className + ' stretched-link border border-' + border}>
        {bullet && <BulletIcon />}
        {children}
      </Badge>
    </Link>
    :
    <Badge bg={bg} text={text} onClick={onClick} className={className + ' border border-' + border + (onClick ? ' pe-link' : '')}>
      {bullet && <BulletIcon />}
      {children}
    </Badge>
  );
};

// 🎨 color mapping based on status
const STATUS_COLORS: Record<string, string> = {
  'Created': '#BB86D3',          // purple
  'Pending': '#E54F9A',          // pink
  'Waiting for Quote': '#FFA805',// yellow
  'Quote Requested': '#FFA805',  // yellow
  'Quote Available': '#528EFF',  // blue
  'Quote Accepted': '#528EFF',   // blue
  'Project Scheduled': '#2AA55E',// green
  'In Progress': '#2AA55E',      // green
  'Report Available': '#528EFF', // blue
  'Closed': '#888888',           // gray for finished
  // Retest request statuses. They share 'Quote Requested', 'Quote Available' and 'In Progress'
  // with a project, but end in their own two states — without these they fall back to the
  // neutral '#ccc' and a finished retest looks indistinguishable from an unknown one.
  'Complete': '#2AA55E',         // green
  'Cancelled': '#888888',        // gray for finished
};

interface NewLinkBadgeProps {
  bullet?: boolean;
  children: React.ReactNode;
  to?: string | null;
  className?: string;
  status?: string;
   disabled?: boolean;
  onClick?: React.MouseEventHandler;
}

export const NewLinkBadge: React.FC<NewLinkBadgeProps> = ({
  bullet,
  children,
  to = null,
  className,
  status = 'Created',
  disabled = false,
  onClick
}) => {
  const color = STATUS_COLORS[status] || '#ccc';
  const iconStyle = { marginTop: -2, color: color };

  const content = (
    <>
      {bullet && <PelletsIcon style={iconStyle} className={className} />}
      <span>{children}</span>
    </>
  );
   if (!to || disabled) {
    return (
      <span
        className={disabled ? 'disabled-link' : undefined}
        onClick={onClick}
      >
        {content}
      </span>
    );
  }

  return to ? <Link to={to} onClick={onClick}>
      {content}
    </Link> : content;
};

interface FloatingFieldProps {
  type?: any;
  min?: string | number;
  value?: any;
  label: string;
  main?: boolean;
  onChange?: any;
  onFocus?: any;
  feedback?: string;
  negative?: boolean;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  defaultValue?: any;
  placeholder?: string;
  onBlur?: () => void;
  style?: React.CSSProperties;
  children?: React.ReactNode | null;
}

export const FloatingField: React.FC<FloatingFieldProps> = ({
  style,
  label,
  value,
  negative,
  disabled,
  feedback = 'Please complete this required field',
  children,
  onChange,
  defaultValue,
  min,
  main = false,
  type = 'text',
  required = true,
  placeholder = '',
   onBlur,
  className = 'mb-4'
}) => {
  return (
    <FloatingLabel className={className} label={label}>
      {!children &&
        <Form.Control
          type={type}
          value={value}
          style={style}
          autoFocus={main}
          spellCheck='false'
          required={required}
          min={min}
          disabled={disabled}
          onChange={onChange}
           onBlur={onBlur}
          placeholder={placeholder}
          defaultValue={defaultValue}
          as={children ? undefined : 'input'}
          autoComplete={main ? 'new-password' : 'off'}
          {...negative && {
            onBlur: (e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = '' },
            onFocus: (e) => { e.target.style.borderColor = '#fa9c96'; e.target.style.boxShadow = '0 0 0 0.1rem #f99c96' }
          }}
        />
      }
      {children}
      <Form.Control.Feedback type='invalid'>{feedback}</Form.Control.Feedback>
    </FloatingLabel>
  );
};


